/**
 * @description 通用请求器
 * @author aprilcai
 */

import axios, { Method } from 'axios';
import { getVsTk, updateTenvideoAppSession, checkIsLogin } from './util';
import { resultCode, resultMessage } from '../module/entity';
import { isTenvideoPhone } from './ua';

export const prodOrigin = 'https://ivapi.video.qq.com';
export const testOrigin = 'https://ivapi-sandbox.video.qq.com';

let isGlobalTest = false; // 全局是否请求后台测试环境
let isGlobalPreview = false; // 全局是否请求后台预览环境
let previewSession = ''; // 预览环境的session
const DEFAULT_RETRY_TIME = 1; // 默认重试次数
const DEFAULT_TIMEOUT = 20000; // 超时20s

// 获取请求域名
export function getOrigin(isTest?: boolean) {
  if (typeof isTest !== 'undefined') {
    return isTest ? testOrigin : prodOrigin;
  }
  return isGlobalTest ? testOrigin : prodOrigin;
}

// 设置全局环境
export async function setServerEnv({
  isTest,
  isPreview = false,
  session = '',
}: {
  isTest: boolean;
  isPreview: boolean;
  session: string;
}) {
  isGlobalTest = isTest;
  isGlobalPreview = isPreview;
  previewSession = session;
  return {
    isTest,
    isPreview,
    session,
  };
}

// 转换错误码
const transferMessage = (message: string) => {
  if (/CODE_SUCC|Succ/i.test(message)) {
    return 'ok';
  }
  return message || '';
};
// 服务端返回登录错误
const isServerLoginFail = (statusCode: number, serverCode: number) => statusCode === 401 || serverCode === 10002;

// 获取预览session
const getPreviewSession = () => ({
  source: isGlobalPreview ? 'preview' : '',
  session: isGlobalPreview ? previewSession : '',
});

// 获取请求链接
const getRequestUrl = (
  url: string,
  retryCount: number,
  isTest: boolean,
) => `${getOrigin(isTest) + url}?${`r=${retryCount}`}`;
// 发起请求
export async function sendRequest(options: {
  data: any;
  cgi: {
    url: string;
    method: Method;
    contentType?: string;
  };
  retryCount: number;
  maxRetryCount: number;
  isTest: boolean;
}): Promise<any> {
  const { data, cgi, retryCount, maxRetryCount, isTest } = options;
  const { url, method } = cgi;
  data.g_vstk = getVsTk();
  data.r = retryCount;
  const { source, session } = getPreviewSession();
  data.source = source;
  data.session = session;
  const requestUrl = getRequestUrl(url, retryCount, isTest);

  const axiosParam = {
    method,
    url: requestUrl,
    withCredentials: true,
    timeout: DEFAULT_TIMEOUT,
    params: {},
    data: {},
  };

  if (/post/i.test(method)) {
    axiosParam.data = data;
  } else {
    axiosParam.params = data;
  }

  const result: any = await axios(axiosParam).catch((error) => {
    const { response, message } = error;
    // 后台有返回数据，非2xx的错误码，这里把http错误码抛出去
    if (response) {
      return {
        status: response.status,
        statusText: response.statusText,
        data: {
          code: status,
          msg: message,
          result: {},
        },
      };
    }
    // 后台没有返回数据，或者没有返回跨域头等
    return {
      status: -1,
      statusText: message,
      data: {
        code: -1,
        msg: message,
        result: {},
      },
    };
  });

  const { status } = result;
  const { code } = result.data;

  result.data.msg = transferMessage(result.data.msg);

  if (code !== 0 && retryCount < maxRetryCount) {
    if (isTenvideoPhone && isServerLoginFail(status, code)) {
      await updateTenvideoAppSession();
    }
    return sendRequest({ ...options, ...{ retryCount: options.retryCount + 1 } });
  }

  return result.data;
}

// 请求数据
export async function request(param: {
  cgi: {
    url: string;
    method: Method;
    result?: any;
    contentType?: string;
  };
  data: any;
  noLogin?: boolean;
  isTest?: boolean;
  retry?: number;
}) {
  const { cgi, data, noLogin, isTest, retry } = param;
  if (!noLogin && !checkIsLogin()) {
    return {
      code: resultCode.noLogin,
      msg: resultMessage.noLogin,
      result: cgi.result || {},
    };
  }
  const maxRetryCount = retry || DEFAULT_RETRY_TIME;
  return sendRequest({
    data,
    cgi,
    retryCount: 0,
    maxRetryCount,
    isTest,
  });
}
